import { v4 as uuid } from "uuid";
import { RouterOutput } from "../../../../../services/rpc/RpcProvider";

export type TransformTestResult =
  RouterOutput["employees"]["dataSources"]["testTransformCases"][number];

// Test cases are edited as raw JSON text, parsed only when running
export type TestCase = {
  id: string;
  name: string;
  json: string;
};

export type ParsedTestCase = {
  id: string;
  name: string;
  mapped: Record<string, unknown>;
  raw: Record<string, unknown>;
};

export const emptyTestCaseJson = `{
  "mapped": {},
  "raw": {}
}`;

const isObject = (value: unknown): value is Record<string, unknown> =>
  !!value && typeof value === "object" && !Array.isArray(value);

export const newTestCase = (name: string, json = emptyTestCaseJson): TestCase => ({
  id: uuid(),
  name,
  json
});

export const toTestCase = (parsed: Omit<ParsedTestCase, "id"> & { id?: string }): TestCase => ({
  id: parsed.id ?? uuid(),
  name: parsed.name,
  json: JSON.stringify({ mapped: parsed.mapped, raw: parsed.raw }, null, 2)
});

export const parseTestCase = (testCase: TestCase): ParsedTestCase => {
  const parsed = JSON.parse(testCase.json);
  if (!isObject(parsed) || !isObject(parsed.mapped) || !isObject(parsed.raw)) {
    throw new Error(
      `Test case "${testCase.name}" must be an object with "mapped" and "raw" objects`
    );
  }
  return { id: testCase.id, name: testCase.name, mapped: parsed.mapped, raw: parsed.raw };
};

// Accepts a single { mapped, raw } object or an array of them, optionally with names
export const parseUploadedTestCases = (content: string, fileName: string): TestCase[] => {
  const parsed = JSON.parse(content);
  const entries: unknown[] = Array.isArray(parsed) ? parsed : [parsed];

  return entries.map((entry, i) => {
    if (!isObject(entry) || !isObject(entry.mapped) || !isObject(entry.raw)) {
      throw new Error(`Entry ${i + 1} must be an object with "mapped" and "raw" objects`);
    }
    const name =
      typeof entry.name === "string"
        ? entry.name
        : entries.length > 1
        ? `${fileName} #${i + 1}`
        : fileName;
    return toTestCase({ name, mapped: entry.mapped, raw: entry.raw });
  });
};
